import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StarIcon from '@mui/icons-material/Star';
import type {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '../display-image';
import {borderRadius, colors, fontSize, fontWeight, overlay, shadows} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

interface QuoteTestimonial {
    id: string;
    type: 'quote';
    variant: 'tint' | 'dark' | 'plain';
    quote: string;
    name: string;
    profile: string;
    avatar: string | StaticImageData;
    rating?: number;
    tags?: string[];
    /** Clamp the quote to this many lines with an ellipsis. Set it on cards that share a
     *  column, where the full text would push the pair well past its neighbours. */
    clampLines?: number;
    /** Swap a plain card's white fill for the subtle off-white, to set it apart from its neighbours. */
    subtle?: boolean;
}

interface VideoTestimonial {
    id: string;
    type: 'video';
    name: string;
    profile: string;
    thumbnail: string | StaticImageData;
    videoUrl: string;
    duration: string;
}

type Testimonial = QuoteTestimonial | VideoTestimonial;

interface MvpTestimonialsProps {
    heading: string;
    testimonials: Testimonial[];
    videoBadgeText?: string;
    /** Cards per column, left to right (default two each). A column of one stretches its card to the row's height. */
    columnSizes?: [number, number, number];
}

// Heading uses the canonical section-heading scale (`sectionHeadingSx`), which carries
// the shared step-down on laptop-class screens (≤1499px) — same as every section that
// renders through `SectionHeader`. Only colour and spacing are section-specific.
const headingSx = [
    sectionHeadingSx,
    {
        color: colors.text.primary,
        textAlign: 'center',
        mb: 4
    }
] as const;

const gridItemSx = {display: 'flex'} as const;

// Shared hover for every card: a small lift plus a shadow and coloured border. A whole-
// pixel `translateY`, not `scale` — scaling resamples the text and images and blurs them.
// Every card carries a 1px border at rest (transparent where it has none), so the hover
// only recolours it and never shifts the layout.
const HOVER_LIFT_PX = 4;
const cardHoverSx = (hoverBorder: string) =>
    ({
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        '&:hover, &:focus-visible': {
            transform: `translateY(-${HOVER_LIFT_PX}px)`,
            boxShadow: shadows.lg,
            borderColor: hoverBorder
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
            '&:hover, &:focus-visible': {transform: 'none'}
        }
    } as const);

const columnSx = {display: 'flex', flexDirection: 'column', gap: 3, width: '100%'} as const;

const quoteCardSx = (variant: QuoteTestimonial['variant'], fill: boolean, subtle?: boolean) =>
    ({
        display: 'flex',
        flexDirection: 'column',
        // The lone tall card only stretches once the three columns sit side by side;
        // stacked, stretching it just leaves a block of empty space.
        flexGrow: {xs: 0, md: fill ? 1 : 0},
        borderRadius: borderRadius.xl,
        p: {xs: 2.5, md: 3},
        bgcolor:
            variant === 'dark'
                ? colors.primary.deep
                : variant === 'tint'
                ? colors.background.tag
                : subtle
                ? colors.background.panelBlue
                : colors.background.default,
        border: `1px solid ${variant === 'plain' ? colors.neutral.borderLight : 'transparent'}`,
        // The navy card needs a light border to show against its own fill.
        ...cardHoverSx(variant === 'dark' ? colors.accent.blueBorder : colors.primary.link)
    } as const);

// A clamped quote also takes `flex: 1`, so it absorbs whatever height the column has
// spare instead of leaving the gap below the author row.
const quoteTextSx = (variant: QuoteTestimonial['variant'], clampLines?: number) =>
    ({
        color: variant === 'dark' ? colors.text.inverse : colors.text.primary,
        fontSize: variant === 'dark' ? fontSize[16] : fontSize[14],
        lineHeight: 1.6,
        mb: 3,
        ...(clampLines && {
            flex: 1,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: clampLines,
            overflow: 'hidden'
        })
    } as const);

const authorRowSx = {display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto'} as const;

const avatarSx = {
    position: 'relative',
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: borderRadius.circle,
    overflow: 'hidden'
} as const;

const authorNameSx = (onDark: boolean) =>
    ({color: onDark ? colors.text.inverse : colors.text.primary, fontWeight: fontWeight.bold, fontSize: fontSize[14]} as const);

const authorRoleSx = (onDark: boolean) => ({color: onDark ? overlay.whiteSoft : colors.text.muted, fontSize: fontSize[13]} as const);

const starRowSx = {display: 'flex', gap: 0.25, ml: 'auto'} as const;
const starSx = {color: colors.accent.starYellow, fontSize: fontSize[16]} as const;

const tagRowSx = {display: 'flex', flexWrap: 'wrap', gap: 1, ml: 'auto'} as const;
const tagSx = {
    px: 1.5,
    py: 0.5,
    borderRadius: borderRadius.full,
    border: `1px solid ${colors.primary.link}`,
    color: colors.primary.link,
    fontSize: fontSize[12],
    fontWeight: fontWeight.medium,
    whiteSpace: 'nowrap'
} as const;

const videoCardSx = {
    position: 'relative',
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    height: {xs: 260, md: 300},
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    border: '1px solid transparent',
    p: 0,
    textDecoration: 'none',
    ...cardHoverSx(colors.primary.link)
} as const;

const videoScrimSx = {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(0deg, ${overlay.dark55} 0%, ${overlay.darkHover} 55%, ${overlay.darkHover} 100%)`
} as const;

const videoBadgeSx = {
    position: 'absolute',
    top: 16,
    left: 16,
    px: 1.5,
    py: 0.75,
    borderRadius: borderRadius.full,
    bgcolor: colors.background.default,
    color: colors.text.primary,
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const
} as const;

const playButtonSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 56,
    height: 56,
    borderRadius: borderRadius.circle,
    bgcolor: overlay.whiteCard,
    color: colors.primary.navy,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: shadows.lg
} as const;

const videoFooterSx = {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 16,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 1.5,
    textAlign: 'left'
} as const;

const durationSx = {color: colors.text.inverse, fontSize: fontSize[13], fontWeight: fontWeight.medium} as const;

const QuoteCard = memo(function QuoteCard({item, fill = false}: {item: QuoteTestimonial; fill?: boolean}) {
    const onDark = item.variant === 'dark';
    return (
        <Box sx={quoteCardSx(item.variant, fill, item.subtle)}>
            <Typography component="p" className="font-regular-text" sx={quoteTextSx(item.variant, item.clampLines)}>
                {`"${item.quote}"`}
            </Typography>
            <Box sx={authorRowSx}>
                <Box sx={avatarSx}>
                    <DisplayImage src={item.avatar} alt={item.name} fill sizes="40px" style={{objectFit: 'cover'}} />
                </Box>
                <Box>
                    <Typography component="p" className="font-headers" sx={authorNameSx(onDark)}>
                        {item.name}
                    </Typography>
                    <Typography component="p" className="font-regular-text" sx={authorRoleSx(onDark)}>
                        {item.profile}
                    </Typography>
                </Box>
                {item.tags ? (
                    <Box sx={tagRowSx}>
                        {item.tags.map((tag) => (
                            <Box key={tag} component="span" sx={tagSx}>
                                {tag}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    item.rating && (
                        <Box sx={starRowSx}>
                            {Array.from({length: item.rating}).map((_, idx) => (
                                <StarIcon key={idx} sx={starSx} />
                            ))}
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
});

const VideoCard = memo(function VideoCard({item, badgeText}: {item: VideoTestimonial; badgeText: string}) {
    return (
        <Box
            component="a"
            href={item.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Play video testimonial from ${item.name}`}
            sx={videoCardSx}
        >
            <DisplayImage src={item.thumbnail} alt={item.name} fill sizes="(max-width: 900px) 100vw, 33vw" style={{objectFit: 'cover'}} />
            <Box sx={videoScrimSx} />
            <Box component="span" sx={videoBadgeSx}>
                {badgeText}
            </Box>
            <Box sx={playButtonSx}>
                <PlayArrowIcon sx={{fontSize: fontSize[32]}} />
            </Box>
            <Box sx={videoFooterSx}>
                <Box>
                    <Typography component="p" className="font-headers" sx={authorNameSx(true)}>
                        {item.name}
                    </Typography>
                    <Typography component="p" className="font-regular-text" sx={authorRoleSx(true)}>
                        {item.profile}
                    </Typography>
                </Box>
                <Typography component="span" className="font-regular-text" sx={durationSx}>
                    {item.duration}
                </Typography>
            </Box>
        </Box>
    );
});

const renderCard = (item: Testimonial, badgeText: string, fill = false) =>
    item.type === 'video' ? (
        <VideoCard key={item.id} item={item} badgeText={badgeText} />
    ) : (
        <QuoteCard key={item.id} item={item} fill={fill} />
    );

// Three masonry columns: a stacked pair, a single full-height card, another pair.
// `size` is how many cards the column holds, `width` its md grid span (they total 12) —
// the paired columns stay wide while the lone tall card runs narrower. The data is
// authored in this same column order.
// The narrow middle column only earns its keep once there is room for it: at `md` the
// three columns split evenly, because 3/12 of a 900px viewport leaves the middle pair too
// cramped for the clamped quotes to sit level with the videos beside them.
const COLUMNS = [
    {size: 2, md: 4, lg: 4.5},
    {size: 2, md: 4, lg: 3},
    {size: 2, md: 4, lg: 4.5}
];

const MvpTestimonials = ({heading, testimonials, videoBadgeText = 'Video Testimonial', columnSizes}: MvpTestimonialsProps) => {
    const items = testimonials ?? [];
    let cursor = 0;
    const columns = COLUMNS.map(({size: defaultSize, md, lg}, index) => {
        const size = columnSizes?.[index] ?? defaultSize;
        const column = items.slice(cursor, cursor + size);
        cursor += size;
        return {cards: column, md, lg};
    });

    return (
        <Box>
            <MUIContainer>
                <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                    {heading}
                </Typography>

                <Grid container spacing={3}>
                    {columns.map((column, columnIndex) => (
                        <Grid item xs={12} md={column.md} lg={column.lg} key={column.cards[0]?.id ?? columnIndex} sx={gridItemSx}>
                            <Box sx={columnSx}>
                                {column.cards.map((item) =>
                                    renderCard(
                                        item,
                                        videoBadgeText,
                                        column.cards.length === 1 || (item.type === 'quote' && Boolean(item.clampLines))
                                    )
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(MvpTestimonials);
